// @flow weak

import { get } from 'lodash';
import { ROUTE_TO_NEXT, routeTo } from 'redux-router-kit';

import {
  addTruthyParamsToUrl,
  extractParams,
  urlWithNext,
} from '@zapier/url-utils';

import * as ABTest from 'app/entities/ABTest';
import * as Account from 'app/entities/Account';
import * as Onboarding from 'app/entities/Onboarding';
import * as Profile from 'app/entities/Profile';
import { currentUrl } from 'app/common/utils/RouterSelectors';
import {
  isAuthAttemptRequiredForMatchedRoutes,
  isAuthRequiredForMatchedRoutes,
} from 'app/common/utils/RouterUtils';

const forceLoadAccountAndProfile = store =>
  Promise.all([
    store.dispatch(Account.actions.forceLoadCollection()),
    store.dispatch(Profile.actions.forceLoadCollection()),
    store.dispatch(ABTest.actions.forceLoadCollection()),
    store.dispatch(Onboarding.actions.forceLoadCollection()),
  ]);

const createAuthenticationMiddleware = ({ routes }) => {
  const middleware = store => next => action => {
    if (!action) {
      return next(action);
    }

    if (action.type === ROUTE_TO_NEXT) {
      const state = store.getState();
      const hasCurrentAccount = !!Account.selectors.all.currentAccount(state);
      const hasCurrentProfile = !!Profile.selectors.all.currentProfile(state);
      const { routeKey } = action.meta;
      const isAuthRequired = isAuthRequiredForMatchedRoutes(routes, routeKey);
      const isAuthAttemptRequired = isAuthAttemptRequiredForMatchedRoutes(
        routes,
        routeKey
      );

      if (!(hasCurrentAccount && hasCurrentProfile)) {
        if (!isAuthRequired) {
          // When auth isn't required for the route, still attempt to
          // load account and profile data. If `isAuthAttemptRequired`
          // then wait until loading has completed (successfully or not)
          // before routing, otherwise route "immediately".
          const loadingPromise = forceLoadAccountAndProfile(store);
          return (isAuthAttemptRequired
            ? loadingPromise
            : Promise.resolve()
          ).then(() => {
            return next(action);
          });
        }

        // Need to `forceLoadCollection` because `shouldLoad` entity selector
        // could prevent the collection from loading if it's *already* loading,
        // which would immediately go to `then` before collection actually
        // finished loading, causing `isSuccess` to be `false` before it has
        // been accurately determined.
        return forceLoadAccountAndProfile(store).then(() => {
          const isAuthenticated =
            Account.selectors.all.isSuccess(store.getState()) &&
            Profile.selectors.all.isSuccess(store.getState());
          return isAuthRequired && !isAuthenticated
            ? store.dispatch(
                routeTo(urlWithNext('/app/login', action.meta.url))
              )
            : next(action);
        });
      }
    }

    // Check if there was an auth-realm violation (SAML vs regular credentials)
    if (
      // catches an Entity API invalid realm violation
      (get(action, ['error', 'status']) === 403 &&
        get(action, ['error', 'json', 'reason']) === 'invalid_auth_realm') ||
      // catches a graphql API invalid realm violation
      (Array.isArray(get(action, ['result', 'errors'])) &&
        action.result.errors.some(
          e =>
            get(e, [
              'extensions',
              'exception',
              'response',
              'body',
              'reason',
            ]) === 'invalid_auth_realm'
        ))
    ) {
      // There was a violation so force the user to reauthenticate
      const url = currentUrl(store.getState());
      const params = extractParams(url);

      if (!params.reauth) {
        // We only want to dispatch this route if
        // we aren't already in a reauth flow (otherwise we could
        // potentially trigger this multiple times)
        return store.dispatch(
          routeTo(
            addTruthyParamsToUrl(urlWithNext('/app/login', url), {
              reauth: true,
            })
          )
        );
      }
    }

    return next(action);
  };

  middleware.setRoutes = newRoutes => {
    routes = newRoutes;
  };

  return middleware;
};

export default createAuthenticationMiddleware;
